<app-page-shell>
  <app-page-hero
    eyebrow="Transport Planning"
    title="Create Truck Plan"
    subtitle="Start from release demand, then assign scoped drivers and trucks with availability and FIFO context.">
    <div actions>
      <p-button label="Release Pool" icon="pi pi-list" severity="secondary" [outlined]="true" routerLink="/transport-planning/releases"></p-button>
      <p-button label="Truck Plans" icon="fa-solid fa-route" severity="secondary" [outlined]="true" routerLink="/transport-planning/plans"></p-button>
    </div>
  </app-page-hero>

  @if (message) {
    <div class="page-message" [ngClass]="'page-message--' + messageSeverity">
      <i class="page-message__icon" [class]="messageIcon()"></i>
      <span>{{ message }}</span>
    </div>
  }

  <app-page-data-panel
    title="Plan Controls"
    subtitle="Use selected releases from the release pool, or search open releases by need-by window."
    [resultText]="totalAssignedReleases() + ' assigned'">
    <div class="control-grid">
      <p-inputgroup>
        <p-inputgroup-addon><i class="pi pi-bookmark"></i></p-inputgroup-addon>
        <p-iftalabel>
          <input pInputText id="plan-name" [(ngModel)]="planName">
          <label for="plan-name">Plan name</label>
        </p-iftalabel>
      </p-inputgroup>

      <p-inputgroup>
        <p-inputgroup-addon><i class="pi pi-calendar-plus"></i></p-inputgroup-addon>
        <p-iftalabel>
          <input pInputText id="need-from" type="datetime-local" [(ngModel)]="needFrom" [disabled]="selectedReleaseIds.length > 0">
          <label for="need-from">Need from</label>
        </p-iftalabel>
      </p-inputgroup>

      <p-inputgroup>
        <p-inputgroup-addon><i class="pi pi-calendar-times"></i></p-inputgroup-addon>
        <p-iftalabel>
          <input pInputText id="need-to" type="datetime-local" [(ngModel)]="needTo" [disabled]="selectedReleaseIds.length > 0">
          <label for="need-to">Need to</label>
        </p-iftalabel>
      </p-inputgroup>

      <div class="control-actions">
        <p-button label="Refresh Releases" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="isLoading" (onClick)="loadPreview()"></p-button>
        <p-button label="Create Plan" icon="fa-solid fa-route" [loading]="isCreating" [disabled]="totalAssignedReleases() === 0" (onClick)="createPlan()"></p-button>
      </div>
    </div>

    <div class="scope-grid">
      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
          <p-iftalabel class="segmented-ifta">
            <p-selectButton
              [options]="resourceViewOptions"
              [ngModel]="resourceView"
              optionLabel="label"
              optionValue="value"
              (ngModelChange)="onResourceViewChange($event)">
            </p-selectButton>
            <label>Asset scope</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>

      @if (canUseSisterCompanyMode) {
        <div class="scope-field">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select
                inputId="sister-company-filter"
                [options]="sisterCompanyFilterOptions"
                [ngModel]="selectedSisterCompanyId"
                (ngModelChange)="onSisterCompanyFilterChange($event)"
                optionLabel="name"
                optionValue="id"
                appendTo="body"
                placeholder="All sister companies"
                styleClass="w-full">
              </p-select>
              <label for="sister-company-filter">Sister company filter</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      }

      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-filter"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-multiSelect
              inputId="grouping-modes"
              [options]="groupingModeOptions"
              [ngModel]="groupingModes"
              (ngModelChange)="onGroupingModesChange($event)"
              optionLabel="label"
              optionValue="value"
              display="chip"
              appendTo="body"
              placeholder="Choose grouping"
              styleClass="w-full">
            </p-multiSelect>
            <label for="grouping-modes">Group releases by</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
    </div>

    <div class="location-filter-grid">
      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-multiSelect
              inputId="pickup-city-filter"
              [options]="cityOptions"
              [(ngModel)]="pickupCityIds"
              (ngModelChange)="onLocationFiltersChange()"
              optionLabel="cityName"
              optionValue="id"
              display="chip"
              [filter]="true"
              appendTo="body"
              placeholder="Any pickup city"
              styleClass="w-full">
            </p-multiSelect>
            <label for="pickup-city-filter">Pickup cities</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>

      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-multiSelect
              inputId="dropoff-city-filter"
              [options]="cityOptions"
              [(ngModel)]="dropoffCityIds"
              (ngModelChange)="onLocationFiltersChange()"
              optionLabel="cityName"
              optionValue="id"
              display="chip"
              [filter]="true"
              appendTo="body"
              placeholder="Any dropoff city"
              styleClass="w-full">
            </p-multiSelect>
            <label for="dropoff-city-filter">Dropoff cities</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>

      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-map"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-multiSelect
              inputId="pickup-region-filter"
              [options]="regionOptions"
              [(ngModel)]="pickupRegionIds"
              (ngModelChange)="onLocationFiltersChange()"
              optionLabel="name"
              optionValue="id"
              display="chip"
              [filter]="true"
              appendTo="body"
              placeholder="Any pickup region"
              styleClass="w-full">
            </p-multiSelect>
            <label for="pickup-region-filter">Pickup regions</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>

      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-map"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-multiSelect
              inputId="dropoff-region-filter"
              [options]="regionOptions"
              [(ngModel)]="dropoffRegionIds"
              (ngModelChange)="onLocationFiltersChange()"
              optionLabel="name"
              optionValue="id"
              display="chip"
              [filter]="true"
              appendTo="body"
              placeholder="Any dropoff region"
              styleClass="w-full">
            </p-multiSelect>
            <label for="dropoff-region-filter">Dropoff regions</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
    </div>

    <div class="global-assignment-grid">
      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
          <p-iftalabel class="asset-select-ifta">
            <p-select
              inputId="global-driver"
              [options]="availableDrivers"
              [(ngModel)]="globalDriver"
              optionLabel="optionLabel"
              filterBy="searchText"
              [filter]="true"
              [showClear]="true"
              appendTo="body"
              panelStyleClass="asset-select-panel"
              placeholder="Apply driver to all groups"
              styleClass="w-full"
              (ngModelChange)="onGlobalDriverChange()">
              <ng-template let-driver pTemplate="selectedItem">
                @if (driver) {
                  <div class="asset-selected-card">
                    <div class="asset-selected-inline">
                      <strong class="asset-selected-name">{{ driverOptionLabel(driver) }}</strong>
                      <p-tag [value]="availabilityStatusLabel(driverAvailability(driver))" [severity]="availabilitySeverity(driverAvailability(driver))"></p-tag>
                    </div>
                    <small class="asset-selected-subline">{{ availabilitySummaryLine(driverAvailability(driver)) }} | {{ availabilityFifoLine(driverAvailability(driver)) }}</small>
                  </div>
                }
              </ng-template>
              <ng-template let-driver pTemplate="item">
                <div class="asset-option-card">
                  <div class="asset-option-head">
                    <div class="asset-primary">
                      <strong>{{ driverOptionLabel(driver) }}</strong>
                      <span>{{ driverOwnerLine(driver) }}</span>
                    </div>
                    <div class="asset-tag-row">
                      <p-tag [value]="availabilityStatusLabel(driverAvailability(driver))" [severity]="availabilitySeverity(driverAvailability(driver))"></p-tag>
                    </div>
                  </div>
                  <div class="asset-metric-grid">
                    <div class="asset-metric">
                      <label>Source</label>
                      <strong>{{ driverAgencySourceLine(driver) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>Availability</label>
                      <strong>{{ availabilitySummaryLine(driverAvailability(driver)) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>Current Place</label>
                      <strong>{{ availabilityLocationLine(driverAvailability(driver)) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>FIFO Priority</label>
                      <strong>{{ availabilityFifoLine(driverAvailability(driver)) }}</strong>
                    </div>
                  </div>
                </div>
              </ng-template>
            </p-select>
            <label for="global-driver">Global driver</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>

      <div class="scope-field">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
          <p-iftalabel class="asset-select-ifta">
            <p-select
              inputId="global-truck"
              [options]="globalVehicleOptions()"
              [(ngModel)]="globalVehicle"
              optionLabel="optionLabel"
              filterBy="searchText"
              [filter]="true"
              [showClear]="true"
              appendTo="body"
              panelStyleClass="asset-select-panel"
              placeholder="Apply truck to all groups"
              styleClass="w-full"
              (ngModelChange)="onGlobalVehicleChange()">
              <ng-template let-vehicle pTemplate="selectedItem">
                @if (vehicle) {
                  <div class="asset-selected-card">
                    <div class="asset-selected-inline">
                      <strong class="asset-selected-name">{{ vehicleOptionLabel(vehicle) }}</strong>
                      <p-tag [value]="availabilityStatusLabel(vehicleAvailability(vehicle))" [severity]="availabilitySeverity(vehicleAvailability(vehicle))"></p-tag>
                    </div>
                    <small class="asset-selected-subline">{{ availabilitySummaryLine(vehicleAvailability(vehicle)) }} | {{ availabilityFifoLine(vehicleAvailability(vehicle)) }}</small>
                  </div>
                }
              </ng-template>
              <ng-template let-vehicle pTemplate="item">
                <div class="asset-option-card">
                  <div class="asset-option-head">
                    <div class="asset-primary">
                      <strong>{{ vehicleOptionLabel(vehicle) }}</strong>
                      <span>{{ vehicleTypeLine(vehicle) }}</span>
                    </div>
                    <div class="asset-tag-row">
                      <p-tag [value]="availabilityStatusLabel(vehicleAvailability(vehicle))" [severity]="availabilitySeverity(vehicleAvailability(vehicle))"></p-tag>
                    </div>
                  </div>
                  <div class="asset-metric-grid">
                    <div class="asset-metric">
                      <label>Identity</label>
                      <strong>{{ vehicleIdentityLine(vehicle) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>Ownership</label>
                      <strong>{{ vehicleOwnerLine(vehicle) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>Source</label>
                      <strong>{{ vehicleAgencySourceLine(vehicle) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>Availability</label>
                      <strong>{{ availabilitySummaryLine(vehicleAvailability(vehicle)) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>Current Place</label>
                      <strong>{{ availabilityLocationLine(vehicleAvailability(vehicle)) }}</strong>
                    </div>
                    <div class="asset-metric">
                      <label>FIFO Priority</label>
                      <strong>{{ availabilityFifoLine(vehicleAvailability(vehicle)) }}</strong>
                    </div>
                  </div>
                </div>
              </ng-template>
            </p-select>
            <label for="global-truck">Global truck</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>

      <div class="global-assignment-note">
        <i class="pi pi-share-alt"></i>
        <span>{{ globalAssignmentSummary() }}</span>
      </div>
    </div>
  </app-page-data-panel>

  <app-page-data-panel
    title="Order Release Groups"
    subtitle="Orders are grouped by need date, route, vehicle type, assignment mode, and leg role. Assign driver and truck lanes per group."
    [resultText]="(preview?.eligibleReleaseCount || 0) + ' plannable releases'">
    @if (isLoading) {
      <div class="notice">Loading release groups...</div>
    }

    @for (group of groups(); track group.groupKey) {
      <section class="release-group">
        <div class="group-head">
          <div>
            <span class="eyebrow">{{ shortDate(group.needByDate) }}</span>
            <h3>{{ groupTitle(group) }}</h3>
            <div class="group-route-grid">
              <div class="route-block">
                <span class="route-kicker">Pickup</span>
                <strong>{{ pickupLocationLabel(group) }}</strong>
                <small>{{ pickupGeoLabel(group) }}</small>
              </div>
              <div class="route-block">
                <span class="route-kicker">Dropoff</span>
                <strong>{{ dropoffLocationLabel(group) }}</strong>
                <small>{{ dropoffGeoLabel(group) }}</small>
              </div>
            </div>
            <p>{{ groupMeta(group) }}</p>
          </div>
          <div class="group-counts">
            <p-tag [value]="selectedReleaseCount(group) + ' releases'" severity="info"></p-tag>
            <p-tag [value]="selectedLaneCount(group) + ' lanes'" severity="success"></p-tag>
            <p-tag [value]="assignedReleaseCount(group) + ' assigned'" severity="contrast"></p-tag>
          </div>
        </div>

        <div class="lane-strip">
          @if (assetsLoading) {
            <div class="empty-assets">
              <i class="pi pi-spin pi-spinner"></i>
              <div>
                <strong>Loading scoped assets.</strong>
                <span>Drivers, trucks, ownership labels, and FIFO availability are being prepared.</span>
              </div>
            </div>
          } @else {
            @for (lane of lanesFor(group); track lane.id; let laneIndex = $index) {
              <article class="lane-card">
                <div class="lane-card-head">
                  <div>
                    <strong>Lane {{ laneIndex + 1 }}</strong>
                    <span>{{ laneReleaseCount(group, lane) }} release{{ laneReleaseCount(group, lane) === 1 ? '' : 's' }}</span>
                  </div>
                  <div class="lane-actions">
                    <button type="button" class="icon-button" (click)="addLane(group)" aria-label="Add lane">
                      <i class="pi pi-plus"></i>
                    </button>
                    @if (lanesFor(group).length > 1) {
                      <button type="button" class="icon-button danger" (click)="removeLane(group, lane)" aria-label="Remove lane">
                        <i class="pi pi-trash"></i>
                      </button>
                    }
                  </div>
                </div>

                <div class="lane-form">
                  <div class="lane-field">
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
                      <p-iftalabel class="asset-select-ifta">
                        <p-select
                          [inputId]="lane.id + '-driver'"
                          [options]="driverOptionsForGroup(group)"
                          [(ngModel)]="lane.driver"
                          optionLabel="optionLabel"
                          filterBy="searchText"
                          [filter]="true"
                          appendTo="body"
                          panelStyleClass="asset-select-panel"
                          placeholder="Choose driver"
                          styleClass="w-full lane-select"
                          (ngModelChange)="onLaneDriverChange(group, lane)">
                          <ng-template let-driver pTemplate="selectedItem">
                            @if (driver) {
                              <div class="asset-selected-card">
                                <div class="asset-selected-inline">
                                  <strong class="asset-selected-name">{{ driverOptionLabel(driver) }}</strong>
                                  <p-tag [value]="availabilityStatusLabel(driverAvailability(driver))" [severity]="availabilitySeverity(driverAvailability(driver))"></p-tag>
                                </div>
                                <small class="asset-selected-subline">{{ availabilitySummaryLine(driverAvailability(driver)) }} | {{ availabilityFifoLine(driverAvailability(driver)) }}</small>
                              </div>
                            }
                          </ng-template>
                          <ng-template let-driver pTemplate="item">
                            <div class="asset-option-card">
                              <div class="asset-option-head">
                                <div class="asset-primary">
                                  <strong>{{ driverOptionLabel(driver) }}</strong>
                                  <span>{{ driverOwnerLine(driver) }}</span>
                                </div>
                                <div class="asset-tag-row">
                                  <p-tag [value]="availabilityStatusLabel(driverAvailability(driver))" [severity]="availabilitySeverity(driverAvailability(driver))"></p-tag>
                                </div>
                              </div>
                              <div class="asset-metric-grid">
                                <div class="asset-metric">
                                  <label>Source</label>
                                  <strong>{{ driverAgencySourceLine(driver) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>Availability</label>
                                  <strong>{{ availabilitySummaryLine(driverAvailability(driver)) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>Current Place</label>
                                  <strong>{{ availabilityLocationLine(driverAvailability(driver)) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>FIFO Priority</label>
                                  <strong>{{ availabilityFifoLine(driverAvailability(driver)) }}</strong>
                                </div>
                              </div>
                            </div>
                          </ng-template>
                        </p-select>
                        <label [for]="lane.id + '-driver'">Driver</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  </div>

                  <div class="lane-field">
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                      <p-iftalabel class="asset-select-ifta">
                        <p-select
                          [inputId]="lane.id + '-vehicle'"
                          [options]="vehicleOptionsForGroup(group)"
                          [(ngModel)]="lane.vehicle"
                          optionLabel="optionLabel"
                          filterBy="searchText"
                          [filter]="true"
                          appendTo="body"
                          panelStyleClass="asset-select-panel"
                          placeholder="Choose truck"
                          styleClass="w-full lane-select"
                          (ngModelChange)="onLaneVehicleChange(group, lane)">
                          <ng-template let-vehicle pTemplate="selectedItem">
                            @if (vehicle) {
                              <div class="asset-selected-card">
                                <div class="asset-selected-inline">
                                  <strong class="asset-selected-name">{{ vehicleOptionLabel(vehicle) }}</strong>
                                  <p-tag [value]="availabilityStatusLabel(vehicleAvailability(vehicle))" [severity]="availabilitySeverity(vehicleAvailability(vehicle))"></p-tag>
                                </div>
                                <small class="asset-selected-subline">{{ availabilitySummaryLine(vehicleAvailability(vehicle)) }} | {{ availabilityFifoLine(vehicleAvailability(vehicle)) }}</small>
                              </div>
                            }
                          </ng-template>
                          <ng-template let-vehicle pTemplate="item">
                            <div class="asset-option-card">
                              <div class="asset-option-head">
                                <div class="asset-primary">
                                  <strong>{{ vehicleOptionLabel(vehicle) }}</strong>
                                  <span>{{ vehicleTypeLine(vehicle) }}</span>
                                </div>
                                <div class="asset-tag-row">
                                  <p-tag [value]="availabilityStatusLabel(vehicleAvailability(vehicle))" [severity]="availabilitySeverity(vehicleAvailability(vehicle))"></p-tag>
                                </div>
                              </div>
                              <div class="asset-metric-grid">
                                <div class="asset-metric">
                                  <label>Identity</label>
                                  <strong>{{ vehicleIdentityLine(vehicle) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>Ownership</label>
                                  <strong>{{ vehicleOwnerLine(vehicle) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>Source</label>
                                  <strong>{{ vehicleAgencySourceLine(vehicle) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>Availability</label>
                                  <strong>{{ availabilitySummaryLine(vehicleAvailability(vehicle)) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>Current Place</label>
                                  <strong>{{ availabilityLocationLine(vehicleAvailability(vehicle)) }}</strong>
                                </div>
                                <div class="asset-metric">
                                  <label>FIFO Priority</label>
                                  <strong>{{ availabilityFifoLine(vehicleAvailability(vehicle)) }}</strong>
                                </div>
                              </div>
                            </div>
                          </ng-template>
                        </p-select>
                        <label [for]="lane.id + '-vehicle'">Truck</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  </div>
                </div>

                <div class="lane-meta">
                  <div>
                    <strong>{{ lane.driver ? driverOwnerLine(lane.driver) : 'Driver pending' }}</strong>
                    <small>{{ lane.driver ? driverAgencySourceLine(lane.driver) : 'Choose a driver asset and source context.' }}</small>
                  </div>
                  <div>
                    <strong>{{ lane.vehicle ? vehicleOwnerLine(lane.vehicle) : 'Truck pending' }}</strong>
                    <small>{{ lane.vehicle ? vehicleAgencySourceLine(lane.vehicle) : 'Choose a truck asset and source context.' }}</small>
                  </div>
                </div>
              </article>
            }
          }
        </div>

        <div class="release-table-wrap">
          <table class="release-table">
            <thead>
              <tr>
                <th></th>
                <th>Release</th>
                <th>Route</th>
                <th>Load / SKU</th>
                <th>Need By</th>
                <th>Lane</th>
              </tr>
            </thead>
            <tbody>
              @for (release of group.releases; track release.releaseId) {
                <tr>
                  <td>
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="isReleaseSelected(group, release)"
                      (ngModelChange)="toggleRelease(group, release, $event)">
                    </p-checkbox>
                  </td>
                  <td>
                    <strong>{{ release.releaseNumber }}</strong>
                    <small>{{ release.sourceType }} {{ release.sourceNumber || '' }}</small>
                    <p-tag [value]="legLabel(release)" severity="secondary"></p-tag>
                  </td>
                  <td>
                    <div class="route-stack">
                      <strong>Pickup: {{ pickupLocationLabel(release) }}</strong>
                      <small>{{ pickupGeoLabel(release) }}</small>
                      <strong>Dropoff: {{ dropoffLocationLabel(release) }}</strong>
                      <small>{{ dropoffGeoLabel(release) }}</small>
                    </div>
                  </td>
                  <td>
                    <strong>{{ release.loadSummary || 'Load details not set' }}</strong>
                    <small>{{ release.skuSummary || 'SKU details unavailable' }}</small>
                  </td>
                  <td>{{ date(release.needByDate) }}</td>
                  <td class="assignment-cell">
                    @if (lanesFor(group).length > 1) {
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-directions"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <p-select
                            [inputId]="'release-lane-' + release.releaseId"
                            [options]="laneOptions(group)"
                            [ngModel]="releaseLaneId(release)"
                            (ngModelChange)="setReleaseLane(group, release, $event)"
                            optionLabel="label"
                            optionValue="value"
                            appendTo="body"
                            placeholder="Choose lane"
                            styleClass="w-full">
                          </p-select>
                          <label [for]="'release-lane-' + release.releaseId">Lane</label>
                        </p-iftalabel>
                      </p-inputgroup>
                    } @else {
                      <span>{{ isReleaseSelected(group, release) ? 'Lane 1' : 'Not selected' }}</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (group.excludedReleases.length > 0) {
          <div class="excluded-strip">
            <strong>{{ group.excludedReleases.length }} release{{ group.excludedReleases.length === 1 ? '' : 's' }} excluded</strong>
            <span>Third-party and invalid releases remain out of this planner.</span>
          </div>
        }
      </section>
    }

    @if (!isLoading && groups().length === 0) {
      <div class="notice">No plannable order releases matched the selected criteria.</div>
    }
  </app-page-data-panel>
</app-page-shell>
